import { useCallback, useEffect, useMemo, useState } from "react";
import { FileSpreadsheet } from "lucide-react";

import Button from "../components/ui/Button";
import TimeRangePicker from "../components/ui/TimeRangePicker";
import ColumnPicker from "../components/ui/ColumnPicker";
import WeeklyTable from "../components/performance/WeeklyTable";
import { GROUPS, HIDEABLE_COLUMN_KEYS } from "../components/performance/columns";
import useAsyncData from "../hooks/useAsyncData";
import { getPerformance, getWeekDays } from "../utils/api";
import { getPresetRange } from "../utils/dateRanges";
import { isoDay } from "../utils/format";
import { buildWeeklyPerformanceCsv, downloadCsv } from "../utils/csv";

export default function PerformancePage() {
  const [range, setRange] = useState({ preset: "90d", ...getPresetRange("90d") });

  const {
    data: table,
    loading,
    error,
  } = useAsyncData(
    useCallback(() => getPerformance({ start: range.start, end: range.end }), [range]),
    [range],
    { errorMessage: "Failed to load performance data." }
  );
  const weeks = table ? table.periods : null;

  // Days are fetched one week at a time, the first time a row is opened, and
  // then kept — reopening a week costs nothing, and a quarter nobody expands
  // never pulls 90 days it doesn't need.
  //
  // Any number of weeks can be open at once, so loading and errors are tracked
  // per week rather than as single flags: two rows opened in quick succession
  // are two independent requests that can finish, or fail, separately.
  const [openWeeks, setOpenWeeks] = useState(() => new Set());
  const [daysByWeek, setDaysByWeek] = useState({});
  const [loadingWeeks, setLoadingWeeks] = useState(() => new Set());
  const [dayErrors, setDayErrors] = useState({});

  // Display preferences, which deliberately survive a range change — someone
  // who hid six columns to read one thing does not want them back every time
  // they move the dates.
  const [hidden, setHidden] = useState(() => new Set());
  const [productCount, setProductCount] = useState(1);

  // A new range means new weeks, so the cached days belong to rows that are
  // about to disappear. Dropping them here rather than inside the fetch keeps
  // the reset immediate — the old expansions never survive into the new table,
  // not even for the second the request is in flight.
  useEffect(() => {
    setOpenWeeks(new Set());
    setDaysByWeek({});
    setLoadingWeeks(new Set());
    setDayErrors({});
  }, [range]);

  const handleToggleWeek = useCallback(
    (weekIso) => {
      // Read the current state here rather than setting a flag inside the
      // updater below and reading it after. That does happen to work today,
      // because React eagerly evaluates an updater to check whether it can
      // bail out — but only while nothing else is queued on this state. Add a
      // second pending update and the updater is deferred to the next render,
      // the flag is still false when the code below runs, and the fetch
      // silently stops firing. Not worth depending on.
      const wasOpen = openWeeks.has(weekIso);

      setOpenWeeks((current) => {
        const next = new Set(current);
        if (wasOpen) next.delete(weekIso);
        else next.add(weekIso);
        return next;
      });

      // Only fetch on the way open, and only the first time — a week already
      // in the cache reopens instantly, including after a failed attempt has
      // been retried successfully.
      if (wasOpen || daysByWeek[weekIso]) return;

      setDayErrors((current) => {
        const { [weekIso]: _removed, ...rest } = current;
        return rest;
      });
      setLoadingWeeks((current) => new Set(current).add(weekIso));

      getWeekDays(weekIso)
        .then((result) => {
          setDaysByWeek((current) => ({ ...current, [weekIso]: result.periods }));
        })
        .catch((err) => {
          setDayErrors((current) => ({
            ...current,
            [weekIso]: err.message || "Failed to load daily data.",
          }));
        })
        .finally(() => {
          setLoadingWeeks((current) => {
            const next = new Set(current);
            next.delete(weekIso);
            return next;
          });
        });
    },
    [openWeeks, daysByWeek]
  );

  const toggleColumn = useCallback((key) => {
    setHidden((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // How many products any row actually carries — the stepper stops there
  // rather than at a constant, so it never offers a row that renders nothing.
  const maxProducts = useMemo(
    () =>
      Math.max(1, ...(weeks || []).map((w) => (w.products ? w.products.length : 0))),
    [weeks]
  );
  const shownProducts = Math.min(productCount, maxProducts);

  function handleExportCsv() {
    if (!weeks) return;
    downloadCsv(
      `weekly-performance-${isoDay(range.start)}-to-${isoDay(range.end)}.csv`,
      buildWeeklyPerformanceCsv({ range, weeks })
    );
  }

  return (
    <div className="w-full flex flex-col gap-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="font-mono text-sm uppercase tracking-widest">Weekly Performance</h1>
          <p className="font-mono text-xs text-collectors-gray max-w-2xl leading-relaxed">
            Site funnel, trading and ad account on one row per week. Sessions and the
            funnel come from Shopify&rsquo;s own analytics, so they tie out to the
            numbers in the admin; spend, CPC and CPA come from Meta. Click any number of
            weeks to break them into days; use Columns to hide what you are not
            reading.
          </p>
        </div>
        <Button onClick={handleExportCsv} disabled={!weeks || weeks.length === 0}>
          <FileSpreadsheet size={16} className="inline mr-2" />
          Export CSV
        </Button>
      </div>

      <TimeRangePicker value={range} onChange={setRange} />

      <ColumnPicker
        groups={GROUPS}
        hidden={hidden}
        onToggleColumn={toggleColumn}
        onShowAll={() => setHidden(new Set())}
        onHideAll={() => setHidden(new Set(HIDEABLE_COLUMN_KEYS))}
        productCount={shownProducts}
        maxProducts={maxProducts}
        onProductCount={setProductCount}
      />

      {loading && (
        <p className="font-mono text-xs uppercase tracking-widest text-collectors-gray py-6">
          Loading&hellip; this one queries Shopify analytics and Meta, so it takes a few seconds.
        </p>
      )}

      {error && (
        <p className="font-mono text-xs uppercase tracking-widest text-red-700 py-6">{error}</p>
      )}

      {!loading && !error && (
        <WeeklyTable
          weeks={weeks}
          expansion={{
            openWeeks,
            days: daysByWeek,
            loadingWeeks,
            errors: dayErrors,
            onToggle: handleToggleWeek,
          }}
          hidden={hidden}
          productCount={shownProducts}
        />
      )}

      {!loading && !error && weeks && weeks.length > 0 && (
        <p className="font-mono text-[11px] text-collectors-gray leading-relaxed max-w-3xl">
          Conversion rate is not a property of the site while nearly all traffic is
          bought: <span className="font-bold">CVR = CPC &divide; CPA</span> is an exact
          identity, so a week of cheaper clicks shows a lower conversion rate with
          nothing on the site having changed. Read the CVR column next to AOV and the
          top product before reading it on its own. MER is coloured against a 1.80
          breakeven and a 2.40 target.
          <br />
          <br />
          <span className="font-bold">Orders &times; AOV does not equal Revenue</span>,
          and should not: both come from Shopify unchanged, on different bases. Revenue
          is total sales &mdash; everything the customer paid, shipping included. AOV is
          Shopify&rsquo;s own, which excludes shipping and tax. Shipping runs 8&ndash;9%
          of sales here and tax is zero on export orders, so Revenue sits about 8% above
          Orders &times; AOV. Returns pull the other way. Use Revenue for money in and
          AOV only for basket size.
        </p>
      )}
    </div>
  );
}
